import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, Chip, Grid, Box, Typography, Alert } from '@mui/material'
import CodeIcon from '@mui/icons-material/Code'
import VideocamIcon from '@mui/icons-material/Videocam'
import api from '../services/api'

const AboutPage = () => {
  const [stats, setStats] = useState([])
  const [totalCameras, setTotalCameras] = useState(0)
  const [loading, setLoading] = useState(true)

  const technologies = [
    'React',
    'Tailwind CSS',
    'Material UI',
    'Framer Motion',
    'Cloudflare D1',
    'Cloudflare Pages',
  ]

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const cameras = await api.getCameras()

        // Calculate statistics by manufacturer
        const manufacturerCounts = cameras.reduce((acc, camera) => {
          acc[camera.manufacturer] = (acc[camera.manufacturer] || 0) + 1
          return acc
        }, {})

        // Convert to array and sort by count (descending)
        const statsArray = Object.entries(manufacturerCounts)
          .map(([manufacturer, count]) => ({ manufacturer, count }))
          .sort((a, b) => b.count - a.count)

        setStats(statsArray)
        setTotalCameras(cameras.length)
        setLoading(false)
      } catch (error) {
        console.error('Failed to fetch statistics:', error)
        setLoading(false)
      }
    }

    fetchStats()
  }, [])


  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto px-4 py-8 max-w-4xl"
    >
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-center mb-12"
      >
        <motion.div
          animate={{
            rotate: [0, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear',
          }}
          className="inline-block mb-6"
        >
          <CodeIcon sx={{ fontSize: 80, color: '#3b82f6' }} />
        </motion.div>
        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-4">
          About Me
        </h1>
      </motion.div>

      {/* Main content card */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <Card
          sx={{
            backgroundColor: 'rgba(26, 26, 26, 0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: '1rem',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            padding: 2,
          }}
        >
          <CardContent>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <h2 className="text-2xl font-bold text-blue-400 mb-4">
                About Me
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed text-lg">
                I'm just a curious guy from Australia who wanted to create my own database of cameras. 📹🇦🇺
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                This project started as a simple idea to organize and compare CCTV camera specifications
                in one place. What you see here is the result of that curiosity - a searchable, sortable
                database that anyone can use and contribute to.
              </p>

              <h3 className="text-xl font-semibold text-purple-400 mb-3">
                Built With
              </h3>
              <div className="flex flex-wrap gap-2 mb-6">
                {technologies.map((tech, index) => (
                  <motion.div
                    key={tech}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 1 + index * 0.1 }}
                  >
                    <Chip
                      label={tech}
                      sx={{
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                        color: '#60a5fa',
                        fontWeight: 'medium',
                        '&:hover': {
                          backgroundColor: 'rgba(59, 130, 246, 0.3)',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.2s',
                      }}
                    />
                  </motion.div>
                ))}
              </div>

              <h3 className="text-xl font-semibold text-purple-400 mb-3">
                Why This Exists
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Finding detailed specs for CCTV cameras can be frustrating. Manufacturer websites are
                hard to navigate, and comparing models means jumping between multiple sites. So I built
                this - a single place to search, compare, and discover cameras from major manufacturers.
              </p>

              <h3 className="text-xl font-semibold text-purple-400 mb-3">
                Want to Help?
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                If you know camera specs or want to contribute, head over to the <strong>Submit</strong> page
                and upload your data. The more cameras we add, the more useful this becomes for everyone!
              </p>
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Database Statistics */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mt-6"
      >
        <Card
          sx={{
            backgroundColor: 'rgba(26, 26, 26, 0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: '1rem',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            padding: 2,
          }}
        >
          <CardContent>
            <Box className="flex items-center gap-2 mb-4">
              <VideocamIcon sx={{ color: '#3b82f6', fontSize: 32 }} />
              <h2 className="text-2xl font-bold text-blue-400">
                Database Statistics
              </h2>
            </Box>

            {loading ? (
              <Typography className="text-gray-400">Loading statistics...</Typography>
            ) : (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.6, type: 'spring' }}
                  className="mb-6"
                >
                  <Box className="text-center p-6 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-lg border border-blue-500/30">
                    <Typography variant="h2" className="text-5xl font-bold text-blue-400">
                      {totalCameras}
                    </Typography>
                    <Typography variant="h6" className="text-gray-300 mt-2">
                      Total Cameras in Database
                    </Typography>
                  </Box>
                </motion.div>

                <Grid container spacing={2} className="mb-6">
                  {stats.map((stat, index) => (
                    <Grid item xs={6} sm={3} key={stat.manufacturer}>
                      <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.7 + index * 0.1 }}
                      >
                        <Box className="text-center p-4 bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-lg border border-gray-700/50">
                          <Typography variant="h4" className="text-3xl font-bold text-purple-400">
                            {stat.count}
                          </Typography>
                          <Typography variant="body2" className="text-gray-400 mt-1">
                            {stat.manufacturer}
                          </Typography>
                        </Box>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Disclaimer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="mt-6"
      >
        <Alert
          severity="info"
          sx={{
            backgroundColor: 'rgba(26, 26, 26, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            color: '#9ca3af',
            '& .MuiAlert-icon': {
              color: '#3b82f6',
            },
          }}
        >
          <Typography variant="body2" className="text-gray-300">
            <strong>Disclaimer:</strong> This database is created for public use and educational purposes.
            This project is not affiliated with, endorsed by, or connected to Hikvision, Axis Communications,
            Hanwha Vision, i-PRO, or any other camera manufacturer featured in this database. All trademarks
            and brand names belong to their respective owners. Camera specifications are collected from publicly
            available sources and may not reflect the latest updates. Always verify specifications with the
            official manufacturer before making purchase decisions.
          </Typography>
        </Alert>
      </motion.div>

      {/* Buy Me a Coffee */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6 }}
        className="mt-8 flex justify-center"
      >
        <Box
          sx={{
            padding: '20px',
            backgroundColor: 'rgba(26, 26, 26, 0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: '1rem',
            border: '1px solid rgba(255, 221, 0, 0.3)',
          }}
        >
          <a
            href="https://www.buymeacoffee.com/tirabytes"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
              alt="Buy Me A Coffee"
              style={{
                height: '60px',
                width: '217px',
              }}
            />
          </a>
        </Box>
      </motion.div>

      {/* Footer note */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="text-center mt-8 text-gray-500 text-sm"
      >
        <p>Made with ❤️ and lots of animations</p>
      </motion.div>
    </motion.div>
  )
}

export default AboutPage
